import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useCustomPresets } from '../qc/customPresetsContext';
import { presetExists, type PresetId } from '../qc/customPresets';
import { parseConvertParams } from '../convertParams';
import { BatchContext, type BatchValue } from './context';
import type { BatchFileInput, BatchFileResult, BatchJob, BatchTarget } from './types';

/** Seed the batch target from a `?to=` deep link so it matches the Converter's
 *  single-file target (cue-forge-zxw.8); falls back to VTT. */
function initialBatchTarget(): BatchTarget {
  const to = parseConvertParams(typeof window === 'undefined' ? '' : window.location.search).to;
  return to ?? 'vtt';
}

/**
 * Holds batch state above the route so switching modes (which unmounts the
 * Batch route) preserves the queued files, run results, progress, and the
 * settings snapshot of the last run. Mounted once in the AppShell alongside
 * the session/QC providers.
 */
export function BatchProvider({ children }: { children: ReactNode }) {
  const { presets: customs } = useCustomPresets();
  const [files, setFiles] = useState<BatchFileInput[]>([]);
  const [target, setTarget] = useState<BatchTarget>(initialBatchTarget);
  const [shift, setShift] = useState('');
  const [preset, setPreset] = useState<PresetId>('');
  const [autoFix, setAutoFix] = useState(false);
  const [notices, setNotices] = useState<string[]>([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [results, setResults] = useState<BatchFileResult[]>([]);
  const [runJob, setRunJob] = useState<BatchJob | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  // Orphan-id coercion (spec §6, mirrors QcProvider): if the selected custom
  // preset no longer resolves (deleted here or in another tab), fall back to no-QC
  // so the controlled <select> never points at a missing <option>. `''` (no QC)
  // is always valid, so only a non-empty, non-existent id is coerced.
  useEffect(() => {
    if (preset !== '' && !presetExists(preset, customs)) setPreset('');
  }, [preset, customs]);

  const value = useMemo<BatchValue>(
    () => ({
      files,
      setFiles,
      target,
      setTarget,
      shift,
      setShift,
      preset,
      setPreset,
      autoFix,
      setAutoFix,
      notices,
      setNotices,
      running,
      setRunning,
      progress,
      setProgress,
      results,
      setResults,
      runJob,
      setRunJob,
      runError,
      setRunError,
    }),
    [files, target, shift, preset, autoFix, notices, running, progress, results, runJob, runError],
  );

  return <BatchContext.Provider value={value}>{children}</BatchContext.Provider>;
}
